import React, { useCallback, useEffect, useRef, useState } from 'react'
import { api } from '../../app/Api/ApiSlice'
import { useParams, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import OrgPostCard from '../../components/OrgPostCard'
import Loader from '../../components/Loader'
import InfiniteScroll from 'react-infinite-scroll-component'
import { FaQrcode } from 'react-icons/fa'

const PAGE_SIZE = 20

const OrgFeeds = () => {
    const navigate = useNavigate()
    const { organization } = useParams()


    const [currentFeeds, setCurrentFeeds] = useState([])
    const [isLoading, setIsLoading] = useState(true)
    const [hasMore, setHasMore] = useState(true)
    const [postsType, setPostsType] = useState('')
    const [optionsOpen, setOptionsOpen] = useState(false)

    const [loadingMRequest, setLoadingMRequest] = useState(false)
    const [loadingFRequest, setLoadingFRequest] = useState(false)
    const [loadingRequest, setLoadingRequest] = useState(false)

    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);

    const [page, setPage] = useState(1);

    const [isAdmin, setIsAdmin] = useState(false);
    const [isMember, setIsMember] = useState(false);

    /*
     * This prevents FlatList's onEndReached
     * from triggering multiple API requests.
     */
    const isFetchingMore = useRef(false);

    /*
     * Used to prevent old requests from updating
     * the state after a new request has started.
     */
    const requestIdRef = useRef(0);


    /* =========================
       POST TYPES
    ========================== */



    /* =========================
       GET ORGANIZATION POSTS
    ========================== */

    const getOrgFeeds = useCallback(
        async (
            pageNumber = 1,
            type = "",
            append = false
        ) => {

            if (!organization) {
                return;
            }

            /*
             * Generate a unique request ID.
             */
            const requestId = ++requestIdRef.current;

            try {

                if (append) {
                    setIsLoadingMore(true);
                } else if (!isRefreshing) {
                    setIsLoading(true);
                }

                const response = await api.get(
                    `/${organization}/posts`,
                    {
                        params: {
                            postsType: type,
                            page: pageNumber,
                        },
                    }
                );

                if (
                    requestId !== requestIdRef.current
                ) {
                    return;
                }

                const newFeeds =
                    response?.data?.posts || [];

                /*
                 * Update membership status if
                 * returned by the backend.
                 */
                if (
                    typeof response?.data?.isMember !==
                    "undefined"
                ) {
                    setIsMember(
                        response.data.isMember
                    );
                }

                const backendHasMore =
                    response?.data?.hasmore || response.data.posts.length >0;

                if (
                    typeof backendHasMore ===
                    "boolean"
                ) {
                    setHasMore(backendHasMore);
                } else {
                    setHasMore(
                        newFeeds.length > 0
                    );
                }

                /*
                 * Page 1 replaces the feed.
                 *
                 * Later pages append to it.
                 */
                if (pageNumber === 1 || !append) {

                    setCurrentFeeds(newFeeds);

                } else {

                    setCurrentFeeds((previousFeeds) => {

                        /*
                         * Prevent duplicate posts if the
                         * backend accidentally returns
                         * overlapping pages.
                         */
                        const existingIds =
                            new Set(
                                previousFeeds.map(
                                    (post) =>
                                        post?._id?.toString()
                                )
                            );

                        const uniqueNewFeeds =
                            newFeeds.filter(
                                (post) =>
                                    post?._id &&
                                    !existingIds.has(
                                        post._id.toString()
                                    )
                            );

                        return [
                            ...previousFeeds,
                            ...uniqueNewFeeds,
                        ];
                    });
                }

            } catch (error) {
                setHasMore(false)

                console.log(
                    "Error fetching organization posts:",
                    error?.response?.data ||
                    error?.message ||
                    error
                );

            } finally {

                /*
                 * Only change loading states if this
                 * request is still the active request.
                 */
                if (
                    requestId ===
                    requestIdRef.current
                ) {
                    setIsLoading(false);
                    setIsLoadingMore(false);
                    setIsRefreshing(false);
                }
            }
        },
        [organization, isRefreshing]
    );


    /* =========================
       INITIAL LOAD
    ========================== */

    useEffect(() => {

        if (!organization) {
            return;
        }

        setPage(1);
        setHasMore(true);

        getOrgFeeds(
            1,
            postsType,
            false
        );

    }, [organization, postsType]);


    /* =========================
       CHECK ADMIN
    ========================== */

    useEffect(() => {

        if (!organization) {
            return;
        }

        let cancelled = false;

        const checkIsAdmin = async () => {

            try {

                const response = await api.get(
                    `/${organization}/admins/check`
                );

                if (cancelled) {
                    return;
                }

                setIsAdmin(
                    response?.data?.isAdmin ?? false
                );


            } catch (error) {

                if (!cancelled) {
                    setIsAdmin(false);
                }

            }
        };

        checkIsAdmin();

        return () => {
            cancelled = true;
        };

    }, [organization]);


    /* =========================
       REFRESH
    ========================== */

    const handleRefresh = async () => {

        if (isLoadingMore) {
            return;
        }

        setIsRefreshing(true);

        setPage(1);
        setHasMore(true);

        await getOrgFeeds(
            1,
            postsType,
            false
        );
    };


    /* =========================
       LOAD MORE
    ========================== */

    const handleLoadMore = async () => {

        /*
         * Do not load another page if:
         *
         * - already loading
         * - no more posts
         * - refreshing
         * - another load-more request exists
         */
        if (
            isFetchingMore.current ||
            isLoadingMore ||
            isLoading ||
            isRefreshing ||
            !hasMore
        ) {
            return;
        }

        isFetchingMore.current = true;

        const nextPage = page + 1;

        try {

            await getOrgFeeds(
                nextPage,
                postsType,
                true
            );

            setPage(nextPage);
        }catch(error){
            // console.log(error)
        } finally {

            isFetchingMore.current = false;

        }
    };



    /*
     * JOIN AS MEMBER
     */
    const joinAsMember = async (orgName) => {
        try {
            setLoadingMRequest(true)

            await api.post(`/${orgName}/members/join`)

            toast.success('Membership request sent')
        } catch (error) {
            toast.error(
                error?.response?.data?.message ||
                'Failed to send membership request'
            )
        } finally {
            setLoadingMRequest(false)
            setOptionsOpen(false)
        }
    }

    /*
     * JOIN AS FRIEND
     */
    const joinAsFriend = async (orgName) => {
        try {
            setLoadingFRequest(true)

            await api.post(`/${orgName}/friends/join`)

            toast.success('Friendship request sent')
        } catch (error) {
            toast.error(
                error?.response?.data?.message ||
                'Failed to send friendship request'
            )
        } finally {
            setLoadingFRequest(false)
            setOptionsOpen(false)
        }
    }

    /*
     * FOLLOW ORGANIZATION
     */
    const followOrg = async (orgName) => {
        if (orgName) {
            toast.error('Organization details are not available yet')
            return
        }

        try {
            setLoadingRequest(true)

            await api.post(`/${orgName}/follow`, {
                // orgId: id,
            })

            toast.success(`${orgName} followed`)
        } catch (error) {
            toast.error(
                error?.response?.data?.message ||
                'Failed to follow organization'
            )
        } finally {
            setLoadingRequest(false)
            setOptionsOpen(false)
        }
    }


    return (
        <div className="feeds-container page-left-content">
            <div className="feeds-header">
                <div className="feeds-header-content">
                    <div className="feed-header-header">
                        <h2>{organization}</h2>
                    </div>

                    <div className="feed-header-header">
                        <FaQrcode
                            onClick={() =>
                                navigate(
                                    `/${organization}/qrcode`,
                                    {
                                        state: {
                                            url: `https://onnize.com/${organization}`,
                                        },
                                    }
                                )
                            }
                        />
                    </div>

                    <div className="feeds-header-link">
                        <div
                            onClick={() =>
                                navigate(`/${organization}/posts`)
                            }
                            className="nav-link"
                        >
                            <p>post</p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="posts-select">
                <select
                    name="type"
                    value={postsType}
                    onChange={(e) => setPostsType(e.target.value)}
                >
                    <option value="">All</option>
                    <option value="events">Events</option>
                    <option value="projects">Projects</option>
                    <option value="important">Importants</option>
                    <option value="information">Informations</option>
                    <option value="notice">Notices</option>
                    <option value="proposal">Proposals</option>
                    <option value="reminder">Reminders</option>
                    <option value="announcement">Announcements</option>
                    <option value="urgent">Urgents</option>
                    <option value="warning">Warnings</option>
                    <option value="request">Requests</option>
                    <option value="update">Updates</option>
                    <option value="correction">Corrections</option>
                </select>

                <button
                    className="normal-button"
                    onClick={() =>
                        setOptionsOpen((previous) => !previous)
                    }
                >
                    Join
                </button>

                <div
                    className={
                        optionsOpen
                            ? 'join-options-open'
                            : 'join-options-closed'
                    }
                >
                    <button
                        onClick={() => joinAsMember(organization)}
                        disabled={loadingMRequest}
                    >
                        {loadingMRequest ? 'Loading...' : 'Member'}
                    </button>

                    <button
                        onClick={() => joinAsFriend(organization)}
                        disabled={loadingFRequest}
                    >
                        {loadingFRequest ? 'Loading...' : 'Friend'}
                    </button>

                    <button
                        onClick={() => followOrg(organization)}
                        disabled={loadingRequest}
                    >
                        {loadingRequest ? 'Loading...' : 'Follow'}
                    </button>
                </div>
            </div>

            <div className="org-feed-container">
                {currentFeeds.length > 0 ? (
                    <InfiniteScroll
                        dataLength={currentFeeds.length}
                        next={handleLoadMore}
                        hasMore={hasMore}
                        loader={
                            isLoadingMore ? <Loader /> : null
                        }
                        refreshFunction={handleRefresh}
                        pullDownToRefresh
                         scrollableTarget="org-content"
                        pullDownToRefreshThreshold={50}
                        releaseToRefreshContent={
                            <p>Release to refresh</p>
                        }
                        pullDownToRefreshContent={
                            <p>Pull down to refresh</p>
                        }
                        endMessage={
                            <p className="not-found-msg">
                                No more posts to load.
                            </p>
                        }
                    >
                        {currentFeeds.map((post) => (
                            <div
                                key={post._id}
                                className="home-feed-post"
                            >
                                <OrgPostCard orgPost={post} />
                            </div>
                        ))}
                    </InfiniteScroll>
                ) : (
                    !isLoading && (
                        <p className="not-found-msg">
                            No {postsType || 'feeds'} available
                        </p>
                    )
                )}

                {isLoading && <Loader />}
            </div>

            <div className="bottom-box"></div>
        </div>
    )
}

export default OrgFeeds